import { useCallback } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@kizen/kds/Button';
import { Typography } from '@kizen/kds/Typography';
import Modal, {
  ModalHeader,
  ModalBody,
  ModalFooter,
} from '__components/Modals';
import type { ModalConfig, RoutablePageConfig } from '@kizenapps/engine';
import type { ButtonColor } from '@kizen/kds/Colors';
import Loader from 'components/Kizen/Loader';
import styled from '@emotion/styled';
import { scrollbarCss } from '__app/spacing';
import { PluginView } from './PluginView';
import type { PluginViewState } from './PluginView';
import { usePluginViewLoading } from './usePluginViewLoading';

const LoaderOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
`;

const AbsoluteLoader = styled(Loader)`
  background: transparent;
  padding-top: 30% !important;
`;

const ModalBox = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  overflow: hidden;
`;

const FramelessModalBody = styled(ModalBody)`
  display: flex;
  flex-direction: column;
`;

const FramelessModalBox = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1 1 0%;
  width: 100%;
  overflow: hidden;
`;

const ModalScrollArea = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 0%;
  min-height: 0;
  width: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  scroll-padding: 20px 0;
  ${scrollbarCss({})}
`;

interface PluginModalViewProps {
  config: ModalConfig;
  props: any;
  pages?: RoutablePageConfig[];
  pluginApiName: string;
}

interface PluginModalChromeProps {
  config: ModalConfig;
  props: any;
  view: PluginViewState;
  children: ReactNode;
}

const PluginModalChrome = ({
  config,
  props,
  view,
  children,
}: PluginModalChromeProps) => {
  const { onConfirm } = props;
  const { t } = useTranslation();

  const { collectFormData } = view;

  const { loading: blockLoading, isSecondLoad } = usePluginViewLoading(view);

  const handleConfirm = useCallback(() => {
    const { ready, data } = collectFormData() ?? {};

    if (!ready) {
      return;
    }

    onConfirm?.({ formData: data });
  }, [onConfirm, collectFormData]);

  const handleHide = useCallback(
    (...args: any) => {
      props?.onHide?.('close', ...args);
    },
    [props]
  );

  return (
    <>
      {blockLoading && (
        // The overlay sits above the modal (and its backdrop), so it has to
        // handle click-away itself. During the initial load the modal is
        // invisible, so any click is "away" — treat it as a cancel. On a
        // second load the modal is still visible underneath, so clicks
        // shouldn't dismiss it.
        <LoaderOverlay
          data-qa-id="plugin-modal-loading-overlay"
          onClick={isSecondLoad ? undefined : handleHide}
        >
          <AbsoluteLoader loading />
        </LoaderOverlay>
      )}

      <Modal
        size={config.size || 'medium'}
        {...props}
        onHide={handleHide}
        frameless={config.frameless}
        style={{
          // Use opacity so the modal can be mounted rendered with script UI while
          // the loader is active
          opacity: blockLoading && !isSecondLoad ? 0 : 1,
        }}
      >
        {config.frameless ? null : (
          <ModalHeader onClickClose={handleHide}>{config.title}</ModalHeader>
        )}
        {config.frameless ? (
          <FramelessModalBody typeOfContent="plugin">
            <FramelessModalBox>
              <ModalScrollArea data-qa-id="plugin-modal-view-scroll-area">
                {children}
              </ModalScrollArea>
            </FramelessModalBox>
          </FramelessModalBody>
        ) : (
          <ModalBody typeOfContent="plugin">
            <ModalBox>{children}</ModalBox>
          </ModalBody>
        )}
        {config.frameless ? null : (
          <ModalFooter>
            <Button
              variant={config.cancelButton?.variant ?? 'text'}
              color={(config.cancelButton?.color ?? 'secondary') as ButtonColor}
              data-qa-id="plugin-modal-view-cancel"
              onClick={(...args: any) => props?.onHide?.('button', ...args)}
            >
              {config.cancelButton?.label ?? t('Cancel')}
            </Button>
            <Button
              variant={config.confirmButton?.variant}
              color={(config.confirmButton?.color ?? 'primary') as ButtonColor}
              data-qa-id="plugin-modal-view-confirm"
              onClick={handleConfirm}
            >
              {config.confirmButton?.label ?? t('Confirm')}
            </Button>
          </ModalFooter>
        )}
      </Modal>
    </>
  );
};

const NotFound = styled.div`
  padding: 20px;
`;

// Shown when the viewId matches no page, instead of an empty modal
const ViewNotFound = () => {
  const { t } = useTranslation();
  return (
    <NotFound data-qa-id="plugin-modal-view-not-found">
      <Typography>{t('View not found')}</Typography>
    </NotFound>
  );
};

export const PluginModalView = ({
  config,
  props,
  pages,
  pluginApiName,
}: PluginModalViewProps) => (
  <PluginView
    pluginApiName={pluginApiName}
    viewApiName={config.viewId ?? ''}
    args={config.args}
    pages={pages}
    fallback={<ViewNotFound />}
  >
    {(body, view) => (
      <PluginModalChrome config={config} props={props} view={view}>
        {body}
      </PluginModalChrome>
    )}
  </PluginView>
);
